import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import SoursSelector from 'components/SoursSelector/SoursSelector';
import {
  ADDRESS_COUNTRY,
  ADDRESS_STATE,
  CATEGORY,
  SOURCE,
  SPECIFIC_SENSITIVITY,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import i18n from 'i18n';
import { useCreateGeoRiskMutation, useGetGeoRiskCategoriesListQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateCompetitorForm from './hooks/useCreateGeoForm';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IGeoRiskItemCreate from 'store/slices/materialAssessment/interfaces/IGeoRiskCreate';
import type IFieldError from 'store/types/IFieldError';
import type { TGeographicCreateSchema } from 'utils/validators';
import type ICreateGeo from './interfaces/ICreateGeo';

import styles from './scss/CreateGeo.module.scss';

function CreateGeo({
  open, onClose, title, recordId, setOffset, setOpenModal,
}: ICreateGeo) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createGeoRisk] = useCreateGeoRiskMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const { data: geoListCategories } = useGetGeoRiskCategoriesListQuery({
    offset: 0,
  });

  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    fieldsName,
  } = useCreateCompetitorForm({ isPageLoading: isLoading });

  const onCancel = () => {
    onClose();
    reset();
  };

  const {
    onFindCountries, isStatesDisabled, onChangeCountry, onChangeState,
    onFindStates, defaultCountries,
  } = useGeoLocations<TGeographicCreateSchema>({ watch, setValue });

  const onAddCompetitor = (item: IGeoRiskItemCreate) => {
    createGeoRisk({
      maId: recordId,
      body: item,
    }).then(() => {
      toast.success(i18n.t('notifications.createdGeoRisk'));
      setOffset(0);
      setOpenModal(false);
    }).catch((errors) => {
      setFieldsErrors<TGeographicCreateSchema>({ errors, setError, fieldsName });
    });
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddCompetitor({
      countryCode: data[ADDRESS_COUNTRY].value || '',
      categoryName: data[CATEGORY].label || '',
      sources: data[SOURCE].map((source) => ({
        name: source.label,
        link: source.value,
      })) || [],
      regions: data[ADDRESS_STATE]?.map((state) => state.label) || [],
      sensitivityName: data[SPECIFIC_SENSITIVITY].label,
    });
    onCancel();
  });

  const categories = geoListCategories?.data.items.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const specificSensitivityOptions = geoListCategories?.data.items.map((category) => ({
    id: category.id,
    name: category.name,
    options: category.sensitivities.map((sensitivity) => ({
      value: sensitivity.name,
      label: sensitivity.name,
    })),
  }));

  const sourcesOption: IOption[] = [
    { value: 'http://source1.com', label: 'Source 1' },
    { value: 'http://source2.com', label: 'Source 2' },
    { value: 'http://source3.com', label: 'Source 3' },
  ];

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={ADDRESS_COUNTRY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.country')}
                placeholder={t('materialityAssessment.placeholder.selectCountry')}
                value={value?.value ? value : null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeCountry();
                }}
                error={error?.message}
                loadOptions={onFindCountries}
                defaultOptions={defaultCountries}
                isSearchable
                isAsync
                isClearable
                className={styles.asyncSelect}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.category')}
                placeholder={t('materialityAssessment.placeholder.selectCategory')}
                options={categories}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SOURCE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <SoursSelector
                label={t('materialityAssessment.fields.source')}
                placeholder={t('materialityAssessment.placeholder.selectSource')}
                titleModal={t('materialityAssessment.addCustomSource')}
                options={sourcesOption}
                onChange={onChange}
                value={value || []}
                error={(error as IFieldError)?.message}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={ADDRESS_STATE}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                label={t('suppliers.fields.state')}
                placeholder={t('materialityAssessment.placeholder.selectState')}
                value={value || null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeState();
                }}
                error={error?.message}
                loadOptions={onFindStates}
                isSearchable
                isAsync
                isClearable
                className={clsx(styles.asyncSelect, {
                  [styles.inactiveSelect]: isStatesDisabled,
                })}
                isDisabled={isStatesDisabled}
                menuPosition="absolute"
                required
                isMulti
              />
            )}
          />
          <Controller
            control={control}
            name={SPECIFIC_SENSITIVITY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.specificSensitivity')}
                placeholder={t('materialityAssessment.placeholder.selectSpecificSensitivity')}
                options={specificSensitivityOptions?.find(
                  (item) => item.name === watch(CATEGORY)?.label,
                )?.options || []}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateGeo;
